import { InputWithIcon } from '@tempo-io/sui-input';
import { type ChangeEvent, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useCommitOnEvents } from '@/features/items/components/custom-field-value/useCommitOnEvents';

const NUMERIC_INPUT_REGEX = /^[\d,]*\.?\d*$/;

const formatWithCommas = (value: number | null, decimals: number) =>
  value == null ? '' : value.toLocaleString('en-US', { maximumFractionDigits: decimals });

const groupDigits = (raw: string) => {
  const cleaned = raw.replace(/,/g, '');
  if (cleaned === '') return '';
  const [intPart = '', ...decimalParts] = cleaned.split('.');
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  if (decimalParts.length > 0) return `${grouped}.${decimalParts.join('')}`;
  return raw.endsWith('.') ? `${grouped}.` : grouped;
};

const caretAfterNonCommaChars = (formatted: string, count: number) => {
  if (count <= 0) return 0;
  let seen = 0;
  for (let i = 0; i < formatted.length; i++) {
    if (formatted[i] !== ',') seen++;
    if (seen === count) return i + 1;
  }
  return formatted.length;
};

interface UnitInputProps {
  value: number | null;
  unit: string;
  unitSide: 'left' | 'right';
  ariaLabel: string;
  decimals?: number;
  disabled?: boolean;
  min?: number;
  max?: number;
  maxDecimals?: number;
  onCommit: (value: number | null) => void;
  onChange?: (value: number | null) => void;
  testId?: string;
}

export const UnitInput = ({
  value,
  unit,
  unitSide,
  ariaLabel,
  decimals = 0,
  disabled,
  min,
  max,
  maxDecimals,
  onCommit,
  onChange,
  testId,
}: UnitInputProps) => {
  const effectiveMaxDecimals = maxDecimals ?? decimals;
  const [text, setText] = useState(formatWithCommas(value, decimals));
  const isFocusedRef = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const caretRef = useRef<number | null>(null);

  useEffect(() => {
    if (!isFocusedRef.current) {
      setText(formatWithCommas(value, decimals));
    }
  }, [value, decimals]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: must re-run after `text` renders to restore the caret
  useLayoutEffect(() => {
    if (caretRef.current != null && inputRef.current) {
      inputRef.current.setSelectionRange(caretRef.current, caretRef.current);
      caretRef.current = null;
    }
  }, [text]);

  const clamp = (n: number) =>
    Math.min(max ?? Number.POSITIVE_INFINITY, Math.max(min ?? Number.NEGATIVE_INFINITY, n));

  const parse = (raw: string): number | null => {
    const stripped = raw.replace(/,/g, '');
    if (stripped === '' || stripped === '.') return null;
    const parsed = Number.parseFloat(stripped);
    if (Number.isNaN(parsed)) return value;
    return Number(clamp(parsed).toFixed(decimals));
  };

  const { handleBlur, handleKeyDown } = useCommitOnEvents({
    value,
    rawValue: text,
    parse,
    onChange: onCommit,
    inputRef,
  });

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const next = event.target.value;
    if (next !== '' && !NUMERIC_INPUT_REGEX.test(next)) {
      return;
    }
    const cleaned = next.replace(/,/g, '');
    const dotIndex = cleaned.indexOf('.');
    if (dotIndex !== -1 && cleaned.length - dotIndex - 1 > effectiveMaxDecimals) {
      return;
    }
    if (max != null && cleaned !== '' && cleaned !== '.') {
      const candidate = Number.parseFloat(cleaned);
      if (!Number.isNaN(candidate) && candidate > max) {
        return;
      }
    }
    const selectionStart = event.target.selectionStart ?? next.length;
    const nonCommaCharsBeforeCaret = next.slice(0, selectionStart).replace(/,/g, '').length;
    const formatted = groupDigits(next);
    caretRef.current = caretAfterNonCommaChars(formatted, nonCommaCharsBeforeCaret);
    setText(formatted);
    onChange?.(parse(formatted));
  };

  const commitAndFormat = () => {
    isFocusedRef.current = false;
    handleBlur();
    setText(formatWithCommas(parse(text), decimals));
  };

  const unitIcon = <span className="text-sm text-text-neutral-secondary">{unit}</span>;
  const sharedProps = {
    ref: inputRef,
    'aria-label': ariaLabel,
    inputMode: 'decimal' as const,
    disabled,
    'data-testid': testId,
    value: text,
    onFocus: () => {
      isFocusedRef.current = true;
    },
    onChange: handleChange,
    onKeyDown: handleKeyDown,
    onBlur: commitAndFormat,
  };

  return unitSide === 'left' ? (
    <InputWithIcon {...sharedProps} className="w-36 text-right" leftIcon={unitIcon} />
  ) : (
    <InputWithIcon {...sharedProps} className="w-36 pr-7 text-right" rightIcon={unitIcon} />
  );
};
